import React, { useState, useEffect } from 'react';
import {
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
  FormHelperText,
  Box,
} from '@mui/material';

// Lista de países com códigos de discagem e emojis de bandeiras
const countries = [
  { code: 'BR', name: 'Brasil', dialCode: '55', flag: '🇧🇷' },
  { code: 'US', name: 'Estados Unidos', dialCode: '1', flag: '🇺🇸' },
  { code: 'AR', name: 'Argentina', dialCode: '54', flag: '🇦🇷' },
  { code: 'CL', name: 'Chile', dialCode: '56', flag: '🇨🇱' },
  { code: 'CO', name: 'Colômbia', dialCode: '57', flag: '🇨🇴' },
  { code: 'MX', name: 'México', dialCode: '52', flag: '🇲🇽' },
  { code: 'PE', name: 'Peru', dialCode: '51', flag: '🇵🇪' },
  { code: 'UY', name: 'Uruguai', dialCode: '598', flag: '🇺🇾' },
  { code: 'PY', name: 'Paraguai', dialCode: '595', flag: '🇵🇾' },
  { code: 'BO', name: 'Bolívia', dialCode: '591', flag: '🇧🇴' },
  { code: 'EC', name: 'Equador', dialCode: '593', flag: '🇪🇨' },
  { code: 'VE', name: 'Venezuela', dialCode: '58', flag: '🇻🇪' },
  { code: 'PT', name: 'Portugal', dialCode: '351', flag: '🇵🇹' },
  { code: 'ES', name: 'Espanha', dialCode: '34', flag: '🇪🇸' },
  { code: 'FR', name: 'França', dialCode: '33', flag: '🇫🇷' },
  { code: 'IT', name: 'Itália', dialCode: '39', flag: '🇮🇹' },
  { code: 'DE', name: 'Alemanha', dialCode: '49', flag: '🇩🇪' },
  { code: 'GB', name: 'Reino Unido', dialCode: '44', flag: '🇬🇧' },
  { code: 'CA', name: 'Canadá', dialCode: '1', flag: '🇨🇦' },
];

export const LandingPagePhoneInput = ({ 
  value, 
  onChange, 
  label,
  name,
  placeholder,
  required,
  error,
  helperText,
  margin,
  InputProps,
  sx,
  ...restProps 
}) => {
  const [selectedCountry, setSelectedCountry] = useState('BR');
  const [phoneNumber, setPhoneNumber] = useState('');

  // Detectar país baseado no valor inicial
  useEffect(() => {
    if (value && typeof value === 'string') {
      const cleanValue = value.replace(/\D/g, '');
      
      // Tentar detectar o país pelo código
      for (const country of countries) {
        if (cleanValue.startsWith(country.dialCode)) {
          setSelectedCountry(country.code);
          setPhoneNumber(cleanValue.substring(country.dialCode.length));
          break;
        }
      }
    }
  }, [value]);

  // Formatação de telefone brasileiro
  const formatBrazilianPhone = (number) => {
    const clean = number.replace(/\D/g, '');
    if (clean.length <= 2) return clean;
    if (clean.length <= 7) return clean.replace(/(\d{2})(\d+)/, '($1) $2');
    if (clean.length <= 11) return clean.replace(/(\d{2})(\d{5})(\d+)/, '($1) $2-$3');
    return clean.replace(/(\d{2})(\d{5})(\d{4})(\d*)/, '($1) $2-$3');
  };

  // Formatação para outros países (formato genérico)
  const formatInternationalPhone = (number) => {
    const clean = number.replace(/\D/g, '');
    if (clean.length <= 3) return clean;
    if (clean.length <= 6) return clean.replace(/(\d{3})(\d+)/, '$1 $2');
    if (clean.length <= 10) return clean.replace(/(\d{3})(\d{3})(\d+)/, '$1 $2 $3');
    return clean.replace(/(\d{3})(\d{3})(\d{4})(\d*)/, '$1 $2 $3');
  };

  const formatPhoneNumber = (number, countryCode) => {
    if (countryCode === 'BR') {
      return formatBrazilianPhone(number);
    }
    return formatInternationalPhone(number);
  };

  const handlePhoneChange = (e) => {
    const inputValue = e.target.value;
    const cleanNumber = inputValue.replace(/\D/g, '');
    
    // Limitar tamanho baseado no país
    const maxLength = selectedCountry === 'BR' ? 11 : 15;
    if (cleanNumber.length > maxLength) return;
    
    const formattedNumber = formatPhoneNumber(inputValue, selectedCountry);
    setPhoneNumber(formattedNumber);
    
    // Criar o valor completo com código do país
    const country = countries.find(c => c.code === selectedCountry);
    const fullNumber = `+${country.dialCode}${cleanNumber}`;
    
    // Criar evento sintético para manter compatibilidade
    const syntheticEvent = {
      target: {
        name: name,
        value: fullNumber,
        type: 'tel'
      }
    };
    
    if (onChange) {
      onChange(syntheticEvent);
    }
  };

  const handleCountryChange = (e) => {
    const newCountryCode = e.target.value;
    setSelectedCountry(newCountryCode);
    
    // Reformatar número atual para o novo país
    if (phoneNumber) {
      const cleanNumber = phoneNumber.replace(/\D/g, '');
      const formattedNumber = formatPhoneNumber(cleanNumber, newCountryCode);
      setPhoneNumber(formattedNumber);
      
      const country = countries.find(c => c.code === newCountryCode);
      const fullNumber = `+${country.dialCode}${cleanNumber}`;
      
      const syntheticEvent = {
        target: {
          name: name,
          value: fullNumber,
          type: 'tel'
        }
      };
      
      if (onChange) {
        onChange(syntheticEvent);
      }
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', ...sx }}>
      <TextField
        variant="outlined"
        label={label}
        placeholder={placeholder || "Número de telefone"}
        value={phoneNumber}
        onChange={handlePhoneChange}
        type="tel"
        name={name}
        required={required}
        error={error}
        margin={margin}
        fullWidth
        sx={{
          maxWidth: '100%',
          '& .MuiOutlinedInput-root': {
            overflow: 'hidden',
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Select
                value={selectedCountry}
                onChange={handleCountryChange}
                sx={{
                  width: 'auto',
                  minWidth: { xs: '70px', sm: '80px' },
                  maxWidth: { xs: '90px', sm: '100px' },
                  '& .MuiSelect-select': {
                    padding: { xs: '2px 4px', sm: '4px 8px' },
                    display: 'flex',
                    alignItems: 'center',
                  },
                  '& fieldset': {
                    border: 'none',
                  },
                }}
                renderValue={(value) => {
                  const country = countries.find(c => c.code === value);
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <span style={{ fontSize: { xs: '14px', sm: '16px' } }}>{country?.flag}</span>
                      <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        +{country?.dialCode}
                      </Typography>
                    </Box>
                  );
                }}
              >
                {countries.map((country) => (
                  <MenuItem key={country.code} value={country.code}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <span style={{ fontSize: '16px' }}>{country.flag}</span>
                      <Typography sx={{ flex: 1 }}>{country.name}</Typography>
                      <Typography color="text.secondary">+{country.dialCode}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </InputAdornment>
          ),
          // Mesclar com InputProps customizados (como ícones de validação)
          ...(InputProps?.endAdornment && {
            endAdornment: InputProps.endAdornment
          }),
        }}
        {...restProps}
      />
      {helperText && (
        <FormHelperText error={error}>
          {helperText}
        </FormHelperText>
      )}
    </Box>
  );
};